/**
 * Platform-bugs admin page.
 *
 * Two views — list (DataTable with bulk select) and kanban (drag-to-
 * update by status). Drawer pulls full detail and exposes the
 * fixed-in-version picker, customer attribution (other rows that
 * share the same summary, a heuristic for content-hash siblings),
 * and the full AI-doctor context.
 *
 * Status flow:
 *   new → investigating → fixed → verified
 *                       ↳ wontfix
 */
import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import {
  AlertTriangle, Bug, ListIcon, KanbanSquare, RefreshCw, Search,
  Trash2, UserCircle, CheckCircle2, GitBranch,
} from 'lucide-react';
import {
  AdminApiClient, 
  type PlatformBugRow, type PlatformBugDetailRow, type TriageStatus,
} from '../api';
import { userMessageFor, failureFrom, type Failure } from '../lib/api-state';
import { usePermissions } from '../lib/permissions';
import { AccessNotice } from '../components/ui/AccessNotice';
import { useSession } from '../session';
import { PageHeader } from '../components/ui/PageHeader';
import { HeroPanel, HeroKpi } from '../components/ui/HeroPanel';
import { Button } from '../components/ui/Button';
import { StatusChip } from '../components/ui/StatusChip';
import { DataTable, PrimaryWithSub, type Column } from '../components/ui/DataTable';
import { Drawer } from '../components/ui/Drawer';
import { FetchState } from '../components/ui/FetchState';

const STATUSES: TriageStatus[] = ['new', 'investigating', 'fixed', 'wontfix', 'verified'];

const STATUS_LABEL: Record<TriageStatus, string> = {
  new: 'New',
  investigating: 'Investigating',
  fixed: 'Fixed',
  wontfix: "Won't fix",
  verified: 'Verified',
};

function bandForBug(status: TriageStatus): 'critical' | 'degraded' | 'healthy' | 'neutral' {
  switch (status) {
    case 'new': return 'critical';
    case 'investigating': return 'degraded';
    case 'fixed': return 'healthy';
    case 'verified': return 'healthy';
    case 'wontfix': return 'neutral';
  }
}

export function PlatformBugsPage(): JSX.Element {
  const { state } = useSession();
  const [rows, setRows] = useState<PlatformBugRow[] | null>(null);
  const [err, setErr] = useState<Failure | null>(null);
  const [version, setVersion] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [view, setView] = useState<'list' | 'kanban'>('list');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<TriageStatus | 'all' | 'open'>('open');
  const [versionFilter, setVersionFilter] = useState<string>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const perms = usePermissions();
  const canWrite = perms.canWrite;
  const canDelete = perms.canOwner;

  const api = useMemo(
    () => new AdminApiClient({ baseUrl: '', getToken: () => state.token }),
    [state.token],
  );

  useEffect(() => {
    setRefreshing(true);
    api
      .platformBugs()
      .then((r) => { setRows(r.platform_bugs); setErr(null); })
      .catch((e) => setErr(failureFrom(e, 'platform bugs')))
      .finally(() => setRefreshing(false));
  }, [api, version]);

  const onRefresh = (): void => setVersion((v) => v + 1);

  const counts = useMemo(() => {
    const c = { new: 0, investigating: 0, fixed: 0, wontfix: 0, verified: 0 };
    for (const r of rows ?? []) {
      const s = r.status as keyof typeof c;
      if (s in c) c[s] = (c[s] ?? 0) + 1;
    }
    return c;
  }, [rows]);

  const versions = useMemo(() => {
    const s = new Set<string>();
    for (const r of rows ?? []) s.add(r.binary_version);
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (rows ?? []).filter((r) => {
      if (statusFilter === 'open' && (r.status === 'fixed' || r.status === 'verified' || r.status === 'wontfix')) return false;
      if (statusFilter !== 'open' && statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (versionFilter !== 'all' && r.binary_version !== versionFilter) return false;
      if (q && !r.summary.toLowerCase().includes(q) && !r.binary_version.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, search, statusFilter, versionFilter]);

  const onDelete = async (id: string): Promise<void> => {
    if (!confirm('Delete this platform bug record? Audit log keeps a snapshot.')) return;
    try {
      await api.deletePlatformBug(id);
      setOpenId(null);
      onRefresh();
    } catch (e) {
      alert(userMessageFor(e));
    }
  };

  const moveTo = async (id: string, status: TriageStatus): Promise<void> => {
    try {
      await api.updatePlatformBug(id, { status });
      // Optimistic local update so the kanban doesn't flicker through a refetch.
      setRows((prev) => prev ? prev.map((r) => r.id === id ? { ...r, status } : r) : prev);
    } catch (e) {
      alert(userMessageFor(e));
      onRefresh();
    }
  };

  const toggleSelected = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = (): void => setSelected(new Set());

  const bulkResolve = async (): Promise<void> => {
    if (!confirm(`Mark ${selected.size} bug(s) as fixed?`)) return;
    const ids = Array.from(selected);
    await bulkUpdate(ids, { status: 'fixed' });
  };

  // Each bug is its own request: report how many actually changed instead of
  // implying all-or-nothing, and always reload so the table shows the truth.
  const bulkUpdate = async (
    ids: string[],
    body: { status?: TriageStatus; assigned_to?: string | null },
  ): Promise<void> => {
    const results = await Promise.allSettled(ids.map((id) => api.updatePlatformBug(id, body)));
    const failed = results.filter((r) => r.status === 'rejected');
    if (failed.length > 0) {
      const first = (failed[0] as PromiseRejectedResult).reason;
      alert(`${ids.length - failed.length} of ${ids.length} updated. ${failed.length} failed: ${userMessageFor(first)}`);
    } else {
      clearSelection();
    }
    onRefresh();
  };

  const bulkAssign = async (): Promise<void> => {
    const handle = prompt('Assign all selected bugs to (staff handle):');
    if (handle == null) return;
    const value = handle.trim();
    const ids = Array.from(selected);
    await bulkUpdate(ids, { assigned_to: value === '' ? null : value });
  };

  return (
    <div className="page">
      <PageHeader
        title="Platform bugs"
        subtitle="Auto-reported engine crashes from customer Vloud installs."
        icon={Bug}
        accent="#f87171"
        actions={
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw width={14} height={14}
              style={{ animation: refreshing ? 'spin 900ms linear infinite' : undefined }} />
            Refresh
          </Button>
        }
      />

      <AccessNotice reason={canWrite ? null : `${perms.writeReason ?? ''} Triage changes are not available to you.`} />

      {rows && <HeroPanel
        icon={AlertTriangle}
        accent="#f87171"
        title="Triage queue"
        subtitle="Group by content-hash; ship a fix, mark fixed-in-version, customer's update-check announces it."
        kpis={[
          <HeroKpi key="new" label="New" value={counts.new}
                   band={counts.new > 0 ? 'critical' : 'neutral'} />,
          <HeroKpi key="inv" label="Investigating" value={counts.investigating}
                   band={counts.investigating > 0 ? 'degraded' : 'neutral'} />,
          <HeroKpi key="fixed" label="Fixed" value={counts.fixed} band={counts.fixed > 0 ? 'healthy' : 'neutral'} />,
          <HeroKpi key="ver" label="Verified" value={counts.verified} band={counts.verified > 0 ? 'healthy' : 'neutral'} />,
          <HeroKpi key="wfx" label="Won't fix" value={counts.wontfix} band="neutral" />,
        ]}
      />}

      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Toolbar
          search={search} onSearch={setSearch}
          statusFilter={statusFilter} onStatusFilter={setStatusFilter}
          counts={counts}
          versions={versions} versionFilter={versionFilter} onVersionFilter={setVersionFilter}
          view={view} onView={setView}
        />

        {canWrite && selected.size > 0 && (
          <BulkBar
            count={selected.size}
            onClear={clearSelection}
            onAssign={() => void bulkAssign()}
            onResolve={() => void bulkResolve()}
          />
        )}

        {view === 'list' ? (
          <BugsTable
            rows={filtered}
            loading={!rows && !err}
            error={err}
            onRetry={onRefresh}
            selected={selected}
            onToggleSelected={toggleSelected}
            canSelect={canWrite}
            onOpen={setOpenId}
          />
        ) : (
          <Kanban
            rows={filtered}
            loading={!rows && !err}
            error={err}
            onRetry={onRefresh}
            onOpen={setOpenId}
            canMove={canWrite}
            onMove={(id, st) => void moveTo(id, st)}
          />
        )}
      </div>

      <BugDrawer
        api={api}
        id={openId}
        rows={rows ?? []}
        onClose={() => setOpenId(null)}
        onSaved={onRefresh}
        canDelete={canDelete}
        canWrite={canWrite}
        onDelete={openId ? () => void onDelete(openId) : undefined}
      />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Toolbar: search + status filter chips + version dropdown + view toggle
 * ────────────────────────────────────────────────────────────────── */

function Toolbar({
  search, onSearch,
  statusFilter, onStatusFilter, counts,
  versions, versionFilter, onVersionFilter,
  view, onView,
}: {
  search: string; onSearch: (q: string) => void;
  statusFilter: TriageStatus | 'all' | 'open';
  onStatusFilter: (s: TriageStatus | 'all' | 'open') => void;
  counts: Record<TriageStatus, number>;
  versions: string[]; versionFilter: string; onVersionFilter: (v: string) => void;
  view: 'list' | 'kanban'; onView: (v: 'list' | 'kanban') => void;
}): JSX.Element {
  const allCount = (Object.keys(counts) as TriageStatus[])
    .reduce((sum, k) => sum + counts[k], 0);
  const openCount = counts.new + counts.investigating;

  const statusChips: Array<{ key: TriageStatus | 'all' | 'open'; label: string; count: number }> = [
    { key: 'open', label: 'Open',          count: openCount },
    { key: 'all',  label: 'All',           count: allCount },
    { key: 'new',  label: 'New',           count: counts.new },
    { key: 'investigating', label: 'Investigating', count: counts.investigating },
    { key: 'fixed', label: 'Fixed',        count: counts.fixed },
    { key: 'verified', label: 'Verified',  count: counts.verified },
    { key: 'wontfix',  label: "Won't fix", count: counts.wontfix },
  ];

  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <SearchInput value={search} onChange={onSearch} placeholder="Search summary or version…" />
        <select
          value={versionFilter}
          onChange={(e) => onVersionFilter(e.target.value)}
          style={{
            background: 'var(--bg-overlay)',
            border: '1px solid var(--border)',
            color: 'var(--text-primary)',
            padding: '7px 10px',
            borderRadius: 8,
            fontSize: '0.8125rem',
          }}
        >
          <option value="all">All versions</option>
          {versions.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <ChipRow chips={statusChips} value={statusFilter} onChange={onStatusFilter} />
        <ViewToggle view={view} onView={onView} />
      </div>
    </div>
  );
}

function SearchInput({
  value, onChange, placeholder,
}: { value: string; onChange: (v: string) => void; placeholder?: string }): JSX.Element {
  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      <Search
        width={14} height={14}
        style={{ position: 'absolute', left: 10, color: 'var(--text-muted)', pointerEvents: 'none' }}
      />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          padding: '7px 10px 7px 30px',
          background: 'var(--bg-overlay)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          color: 'var(--text-primary)',
          fontSize: '0.8125rem',
          minWidth: 220,
          outline: 'none',
        }}
      />
    </div>
  );
}

function ChipRow<T extends string>({
  chips, value, onChange,
}: {
  chips: Array<{ key: T; label: string; count: number }>;
  value: T;
  onChange: (next: T) => void;
}): JSX.Element {
  return (
    <div
      role="group"
      aria-label="Filters"
      style={{
        display: 'inline-flex',
        gap: 4,
        padding: 4,
        borderRadius: 12,
        border: '1px solid var(--border)',
        background: 'var(--bg-overlay)',
      }}
    >
      {chips.map((c) => {
        const active = c.key === value;
        return (
          <button
            key={c.key}
            aria-pressed={active}
            onClick={() => onChange(c.key)}
            style={{
              display: 'inline-flex',
              gap: 5, alignItems: 'center',
              padding: '5px 9px',
              border: 'none',
              borderRadius: 8,
              background: active ? 'var(--bg-raised)' : 'transparent',
              color: active ? 'var(--text-primary)' : 'var(--text-muted)',
              fontSize: '0.75rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            {c.label}
            <span
              style={{
                fontVariantNumeric: 'tabular-nums',
                fontSize: '0.6875rem',
                color: 'var(--text-muted)',
                background: 'var(--bg-overlay)',
                border: '1px solid var(--border)',
                padding: '0 6px',
                borderRadius: 999,
                minWidth: 18, textAlign: 'center',
              }}
            >{c.count}</span>
          </button>
        );
      })}
    </div>
  );
}

function ViewToggle({
  view, onView,
}: { view: 'list' | 'kanban'; onView: (v: 'list' | 'kanban') => void }): JSX.Element {
  const Btn = ({ v, Icon, label }: { v: 'list' | 'kanban'; Icon: typeof ListIcon; label: string }): JSX.Element => {
    const active = view === v;
    return (
      <button
        onClick={() => onView(v)}
        aria-pressed={active}
        title={label}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '6px 10px',
          border: 'none', borderRadius: 8,
          background: active ? 'var(--bg-raised)' : 'transparent',
          color: active ? 'var(--text-primary)' : 'var(--text-muted)',
          cursor: 'pointer',
          fontSize: '0.75rem', fontWeight: 500,
        }}
      >
        <Icon width={14} height={14} />
        {label}
      </button>
    );
  };
  return (
    <div
      style={{
        display: 'inline-flex',
        padding: 4,
        borderRadius: 10,
        border: '1px solid var(--border)',
        background: 'var(--bg-overlay)',
      }}
    >
      <Btn v="list" Icon={ListIcon} label="List" />
      <Btn v="kanban" Icon={KanbanSquare} label="Board" />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Bulk action bar
 * ────────────────────────────────────────────────────────────────── */

function BulkBar({
  count, onClear, onAssign, onResolve,
}: { count: number; onClear: () => void; onAssign: () => void; onResolve: () => void }): JSX.Element {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 14px',
        borderRadius: 10,
        background: 'rgba(99, 102, 241, 0.10)',
        border: '1px solid rgba(99, 102, 241, 0.35)',
      }}
    >
      <CheckCircle2 width={14} height={14} color="var(--accent)" />
      <span style={{ fontSize: '0.8125rem', fontWeight: 500 }}>
        {count} selected
      </span>
      <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
        <Button variant="outline" size="sm" onClick={onAssign}>
          <UserCircle width={14} height={14} />
          Assign…
        </Button>
        <Button variant="default" size="sm" onClick={onResolve}>
          <CheckCircle2 width={14} height={14} />
          Mark fixed
        </Button>
        <Button variant="ghost" size="sm" onClick={onClear}>
          Clear
        </Button>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * List view (DataTable + checkboxes)
 * ────────────────────────────────────────────────────────────────── */

function BugsTable({
  rows, loading, error, onRetry,
  selected, onToggleSelected, canSelect, onOpen,
}: {
  rows: PlatformBugRow[];
  loading: boolean;
  error: Failure | null;
  onRetry: () => void;
  selected: Set<string>;
  onToggleSelected: (id: string) => void;
  canSelect: boolean;
  onOpen: (id: string) => void;
}): JSX.Element {
  const columns: Column<PlatformBugRow>[] = [
    ...(canSelect ? [{
      key: 'sel',
      header: '',
      width: 32,
      cell: (r: PlatformBugRow) => (
        <input
          type="checkbox"
          checked={selected.has(r.id)}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onToggleSelected(r.id)}
          aria-label="Select row"
        />
      ),
    }] : []),
    {
      key: 'summary',
      header: 'Summary',
      sortable: true,
      sortValue: (r) => r.summary,
      cell: (r) => (
        <PrimaryWithSub
          primary={r.summary}
          sub={`license ${r.license_id.slice(0, 8)}…`}
        />
      ),
    },
    {
      key: 'version',
      header: 'Version',
      sortable: true,
      sortValue: (r) => r.binary_version,
      cell: (r) => (
        <code style={{
          fontSize: '0.75rem', color: 'var(--text-muted)',
          background: 'var(--bg-overlay)',
          padding: '2px 6px', borderRadius: 4,
          border: '1px solid var(--border)',
        }}>{r.binary_version}</code>
      ),
    },
    {
      key: 'hits',
      header: 'Reports',
      align: 'right',
      sortable: true,
      sortValue: (r) => r.occurrences,
      cell: (r) => (
        <span title={`Reported ${r.occurrences} time${r.occurrences === 1 ? '' : 's'}`}>
          <OccurrenceBadge count={r.occurrences} />
        </span>
      ),
    },
    {
      key: 'last',
      header: 'Last reported',
      align: 'right',
      sortable: true,
      sortValue: (r) => Date.parse(r.last_seen_at),
      cell: (r) => (
        <span
          title={fmtAbsolute(r.last_seen_at)}
          style={{
            color: 'var(--text-primary)',
            fontSize: '0.75rem',
            fontWeight: r.occurrences > 1 ? 500 : 400,
          }}
        >
          {fmtRelative(r.last_seen_at)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => (
        <StatusChip band={bandForBug(r.status as TriageStatus)}>
          {STATUS_LABEL[r.status as TriageStatus] ?? r.status}
        </StatusChip>
      ),
    },
  ];

  return (
    <DataTable
      rows={rows}
      columns={columns}
      rowKey={(r) => r.id}
      onRowClick={(r) => onOpen(r.id)}
      loading={loading}
      error={error}
      onRetry={onRetry}
      emptyTitle="No bugs match"
      emptyDescription="Adjust filters or search terms to see more."
    />
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Kanban view
 * ────────────────────────────────────────────────────────────────── */

function Kanban({
  rows, loading, error, onRetry, onOpen, canMove, onMove,
}: {
  rows: PlatformBugRow[];
  loading: boolean;
  error: Failure | null;
  onRetry: () => void;
  onOpen: (id: string) => void;
  canMove: boolean;
  onMove: (id: string, status: TriageStatus) => void;
}): JSX.Element {
  return (
    <FetchState
      loading={loading}
      error={error}
      empty={!loading && !error && rows.length === 0}
      onRetry={onRetry}
      emptyTitle="No bugs match"
      emptyDescription="Adjust filters or search terms to see more."
      skeleton="cards"
    >
      <div
        style={{
          display: 'grid',
          gap: 12,
          gridTemplateColumns: `repeat(${STATUSES.length}, minmax(200px, 1fr))`,
          overflowX: 'auto',
        }}
      >
        {STATUSES.map((s) => (
          <KanbanColumn
            key={s}
            status={s}
            rows={rows.filter((r) => r.status === s)}
            canMove={canMove}
            onOpen={onOpen}
            onMove={canMove ? onMove : () => { /* read-only role */ }}
          />
        ))}
      </div>
    </FetchState>
  );
}

function KanbanColumn({
  status, rows, canMove, onOpen, onMove,
}: {
  status: TriageStatus;
  rows: PlatformBugRow[];
  canMove: boolean;
  onOpen: (id: string) => void;
  onMove: (id: string, status: TriageStatus) => void;
}): JSX.Element {
  const [dragOver, setDragOver] = useState(false);

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        if (!dragOver) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const id = e.dataTransfer.getData('text/plain');
        if (id) onMove(id, status);
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 10,
        borderRadius: 12,
        background: dragOver ? 'rgba(99, 102, 241, 0.10)' : 'var(--bg-overlay)',
        border: `1px ${dragOver ? 'solid' : 'dashed'} ${dragOver ? 'var(--accent)' : 'var(--border)'}`,
        minHeight: 200,
        transition: 'background 120ms ease, border-color 120ms ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <StatusChip band={bandForBug(status)} size="md">
          {STATUS_LABEL[status]}
        </StatusChip>
        <span
          style={{
            fontSize: '0.6875rem', color: 'var(--text-muted)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {rows.length}
        </span>
      </div>

      {rows.map((r) => (
        <KanbanCard key={r.id} row={r} draggable={canMove} onOpen={() => onOpen(r.id)} />
      ))}
      {rows.length === 0 && (
        <div
          style={{
            color: 'var(--text-muted)',
            fontSize: '0.75rem',
            textAlign: 'center',
            padding: 16,
          }}
        >
          {canMove ? 'drop here' : 'none'}
        </div>
      )}
    </div>
  );
}

function KanbanCard({
  row, draggable, onOpen,
}: { row: PlatformBugRow; draggable: boolean; onOpen: () => void }): JSX.Element {
  return (
    <button
      type="button"
      draggable={draggable}
      onDragStart={(e) => e.dataTransfer.setData('text/plain', row.id)}
      onClick={onOpen}
      style={{
        textAlign: 'left',
        padding: 10,
        borderRadius: 10,
        background: 'var(--bg-raised)',
        border: '1px solid var(--border)',
        cursor: draggable ? 'grab' : 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        <span
          style={{
            fontSize: '0.8125rem', fontWeight: 500,
            color: 'var(--text-primary)',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            flex: 1,
            minWidth: 0,
          }}
        >
          {row.summary}
        </span>
        <OccurrenceBadge count={row.occurrences} />
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <code style={{
          fontSize: '0.6875rem', color: 'var(--text-muted)',
          padding: '1px 5px', background: 'var(--bg-overlay)',
          borderRadius: 4, border: '1px solid var(--border)',
        }}>{row.binary_version}</code>
        <span
          title={`Last reported: ${fmtAbsolute(row.last_seen_at)}`}
          style={{
            fontSize: '0.6875rem', color: 'var(--text-muted)', marginLeft: 'auto',
            fontWeight: row.occurrences > 1 ? 500 : 400,
          }}
        >
          last {fmtRelative(row.last_seen_at)}
        </span>
      </div>
    </button>
  );
}

/**
 * "Reported × N" badge — shows next to the bug title in kanban cards
 * and in the drawer header. Tone scales with recurrence so triage can
 * tell a one-off from a recurring fire at a glance:
 *   1     → muted (just the one report)
 *   2-4   → warning (recurring)
 *   5+    → critical (chronic — bumps to the top of triage)
 */
function OccurrenceBadge({ count }: { count: number }): JSX.Element {
  const tone = count >= 5 ? '#ef4444'
             : count >= 2 ? '#fb923c'
                          : '#94a3b8';
  return (
    <span
      title={`Reported ${count} time${count === 1 ? '' : 's'}`}
      style={{
        flex: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 7px',
        borderRadius: 999,
        fontSize: '0.6875rem',
        fontWeight: 600,
        background: `${tone}1f`,
        color: tone,
        border: `1px solid ${tone}55`,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      ×{count}
    </span>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Drawer with full triage form + customer attribution
 * ────────────────────────────────────────────────────────────────── */

function BugDrawer({
  api, id, rows, onClose, onSaved, canDelete, canWrite, onDelete,
}: {
  api: AdminApiClient;
  id: string | null;
  rows: PlatformBugRow[];
  onClose: () => void;
  onSaved: () => void;
  canDelete: boolean;
  canWrite: boolean;
  onDelete?: () => void;
}): JSX.Element {
  const [detail, setDetail] = useState<PlatformBugDetailRow | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<{
    status: TriageStatus;
    assigned_to: string;
    triage_notes: string;
    fixed_in_version: string;
  }>({ status: 'new', assigned_to: '', triage_notes: '', fixed_in_version: '' });
  const lastIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!id) { setDetail(null); return; }
    if (lastIdRef.current === id) return;
    lastIdRef.current = id;
    setDetail(null);
    setErr(null);
    api.platformBug(id)
      .then((d) => {
        setDetail(d);
        setDraft({
          status: d.status as TriageStatus,
          assigned_to: d.assigned_to ?? '',
          triage_notes: d.triage_notes ?? '',
          fixed_in_version: d.fixed_in_version ?? '',
        });
      })
      .catch((e) => setErr(failureFrom(e, 'this bug').message));
  }, [id, api]);

  const save = async (): Promise<void> => {
    if (!id) return;
    setSaving(true);
    setErr(null);
    try {
      const body = {
        status: draft.status,
        assigned_to: draft.assigned_to.trim() === '' ? null : draft.assigned_to,
        triage_notes: draft.triage_notes.trim() === '' ? null : draft.triage_notes,
        fixed_in_version: draft.fixed_in_version.trim() === '' ? null : draft.fixed_in_version,
      };
      const updated = await api.updatePlatformBug(id, body);
      setDetail(updated);
      onSaved();
    } catch (e) {
      setErr(userMessageFor(e));
    } finally {
      setSaving(false);
    }
  };

  const related = useMemo(() => {
    if (!detail) return [];
    return rows.filter((r) =>
      r.id !== detail.id && (r.summary === detail.summary || r.binary_version === detail.binary_version),
    );
  }, [rows, detail]);

  return (
    <Drawer
      open={!!id}
      onClose={() => { lastIdRef.current = null; onClose(); }}
      width="lg"
      title={
        detail ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span style={{ flex: 1, minWidth: 0 }}>{detail.summary}</span>
            <OccurrenceBadge count={detail.occurrences} />
          </span>
        ) : 'Triage'
      }
      subtitle={
        detail ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <code>{detail.binary_version}</code>
            <span>·</span>
            <span title={fmtAbsolute(detail.last_seen_at)}>
              last reported {fmtRelative(detail.last_seen_at)}
            </span>
            <span>·</span>
            <span title={fmtAbsolute(detail.first_seen_at)}>
              first {fmtRelative(detail.first_seen_at)}
            </span>
          </span>
        ) : undefined
      }
      footer={
        <>
          {canDelete && onDelete && (
            <Button variant="ghost" size="sm" onClick={onDelete}>
              <Trash2 width={14} height={14} />
              Delete
            </Button>
          )}
          {canWrite ? (
            <Button variant="default" size="sm" onClick={() => void save()} disabled={saving || !detail}>
              {saving ? 'Saving…' : 'Save triage'}
            </Button>
          ) : (
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Triage can be changed by Owner and Admin accounts.
            </span>
          )}
        </>
      }
    >
      {err && <p className="err" role="alert" style={{ fontSize: '0.8125rem' }}>{err}</p>}
      {!detail && !err && <p role="status" style={{ color: 'var(--text-muted)' }}>Loading detail…</p>}

      {detail && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <DrawerSection title="Triage">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Labeled label="Status">
                <select
                  disabled={!canWrite}
                  value={draft.status}
                  onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value as TriageStatus }))}
                  style={inputStyle}
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                </select>
              </Labeled>
              <Labeled label="Assigned to">
                <input
                  readOnly={!canWrite}
                  value={draft.assigned_to}
                  placeholder="staff handle (e.g. alice)"
                  onChange={(e) => setDraft((d) => ({ ...d, assigned_to: e.target.value }))}
                  style={inputStyle}
                />
              </Labeled>
              <Labeled
                label="Fixed in version"
                hint="When set + status=fixed, customer's update-check will surface this."
              >
                <input
                  readOnly={!canWrite}
                  value={draft.fixed_in_version}
                  placeholder="1.4.2 — must match a release.version"
                  onChange={(e) => setDraft((d) => ({ ...d, fixed_in_version: e.target.value }))}
                  style={inputStyle}
                />
              </Labeled>
              <Labeled label="Triage notes" hint="Internal — never sent to the customer.">
                <textarea
                  readOnly={!canWrite}
                  value={draft.triage_notes}
                  onChange={(e) => setDraft((d) => ({ ...d, triage_notes: e.target.value }))}
                  rows={4}
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                />
              </Labeled>
            </div>
          </DrawerSection>

          <DrawerSection title="Customer attribution">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <RelatedRow
                label="Open bug"
                license={detail.license_id}
                version={detail.binary_version}
                hits={detail.occurrences}
                primary
              />
              {related.length > 0 ? (
                related.slice(0, 8).map((r) => (
                  <RelatedRow
                    key={r.id}
                    label={r.summary === detail.summary ? 'Same summary' : 'Same version'}
                    license={r.license_id}
                    version={r.binary_version}
                    hits={r.occurrences}
                  />
                ))
              ) : (
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0 }}>
                  No related reports under the same summary/version.
                </p>
              )}
            </div>
          </DrawerSection>

          {detail.full_context && (
            <DrawerSection title="Full context (AI doctor)">
              <pre
                style={{
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.75rem',
                  background: 'var(--bg-overlay)',
                  padding: 12,
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  maxHeight: 360,
                  overflow: 'auto',
                  margin: 0,
                  fontFamily: 'ui-monospace, monospace',
                }}
              >
                {detail.full_context}
              </pre>
            </DrawerSection>
          )}

          <DrawerSection title="Identifiers">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              <div>content_hash: <code>{detail.content_hash.slice(0, 24)}…</code></div>
              <div>id: <code>{detail.id}</code></div>
              <div>license: <code>{detail.license_id}</code></div>
              {detail.installation_id && (
                <div>installation: <code>{detail.installation_id}</code></div>
              )}
              <div>first seen: {fmtAbsolute(detail.first_seen_at)}</div>
              <div>last seen: {fmtAbsolute(detail.last_seen_at)}</div>
            </div>
          </DrawerSection>
        </div>
      )}
    </Drawer>
  );
}

const inputStyle = {
  width: '100%',
  padding: 8,
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--bg-overlay)',
  color: 'var(--text-primary)',
  fontSize: '0.8125rem',
  outline: 'none',
} as const;

function DrawerSection({
  title, children,
}: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <section>
      <h3
        style={{
          margin: 0, marginBottom: 8,
          fontSize: '0.6875rem', fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.08em',
          color: 'var(--text-muted)',
        }}
      >
        {title}
      </h3>
      {children}
    </section>
  );
}

function Labeled({
  label, hint, children,
}: { label: string; hint?: string; children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{label}</span>
      {children}
      {hint && (
        <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>{hint}</span>
      )}
    </div>
  );
}

function RelatedRow({
  label, license, version, hits, primary,
}: {
  label: string;
  license: string;
  version: string;
  hits: number;
  primary?: boolean;
}): JSX.Element {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 10px',
        borderRadius: 8,
        border: `1px solid ${primary ? 'var(--accent)' : 'var(--border)'}`,
        background: primary ? 'rgba(99, 102, 241, 0.08)' : 'var(--bg-overlay)',
      }}
    >
      <GitBranch width={12} height={12} color="var(--text-muted)" />
      <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <code style={{ fontSize: '0.75rem' }}>license {license.slice(0, 8)}…</code>
      </span>
      <code style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>{version}</code>
      <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
        {hits} hits
      </span>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Helpers
 * ────────────────────────────────────────────────────────────────── */

function fmtRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function fmtAbsolute(iso: string): string {
  return new Date(iso).toISOString().slice(0, 16).replace('T', ' ') + 'Z';
}

